/**
 * <s-custom-text-addon> — Optional add-on checkbox that switches the product between
 * two variants (for example Standard and Personalized) while hiding the variant
 * control itself. Used by the custom-text add-on UI in templates/product.liquid.
 *
 * Required child markup:
 *   input[type="checkbox"][data-check-value]   — The add-on checkbox. data-check-value is
 *                                                the (case-insensitive) option value that
 *                                                "checked" maps to, e.g. "personalized".
 *   input[type="checkbox"][data-uncheck-value] — Same input or a sibling; the option value
 *                                                that "unchecked" maps to, e.g. "standard".
 *
 * Required sibling markup (inside the same <s-variant-picker>):
 *   [data-addon-style-picker]                  — Wrapper around the hidden variant picker
 *                                                for the pricing option. Its radio inputs
 *                                                carry the option value in their value
 *                                                attribute and a data-variant-id used by
 *                                                s-variant-picker to switch variants.
 *
 * Behavior:
 *   On checkbox change, finds the radio in the hidden picker whose value matches the
 *   target option value, checks it, and dispatches a bubbling `change` event so
 *   s-variant-picker performs its normal partial-render variant update. Checked state
 *   and the conditional text field are re-rendered server-side from the selected
 *   variant, so no client state must survive the swap.
 *
 * External coupling:
 *   s-variant-picker change listener — receives the dispatched event.
 */
class CustomTextAddon extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (e) => {
      const checkbox = e.target;
      if (!checkbox || checkbox.type !== 'checkbox') return;
      this.#syncVariant(checkbox);
    });
  }

  #syncVariant(checkbox) {
    const picker = this.closest('s-variant-picker');
    if (!picker) return;
    const stylePicker = picker.querySelector('[data-addon-style-picker]');
    if (!stylePicker) return;

    const targetValue = checkbox.checked
      ? (checkbox.dataset.checkValue || '').toLowerCase()
      : (checkbox.dataset.uncheckValue || '').toLowerCase();
    if (!targetValue) return;

    const radios = stylePicker.querySelectorAll('input[type="radio"]');
    for (const radio of radios) {
      if ((radio.value || '').toLowerCase() !== targetValue) continue;
      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
  }
}

customElements.define('s-custom-text-addon', CustomTextAddon);
